(() => {
  const namespace = 'CozyNookFloatingContact';
  if (window[namespace]) {
    window[namespace].initializeAll(document);
    return;
  }

  const cleanups = new WeakMap();

  const getWidgetRoots = (scope) => {
    const roots = [];
    if (scope.matches && scope.matches('floating-contact-widget')) roots.push(scope);
    scope.querySelectorAll('floating-contact-widget').forEach((root) => roots.push(root));
    return roots;
  };

  const initializeWidget = (root) => {
    if (!root || cleanups.has(root)) return;

    const panel = root.querySelector('.floating-contact__panel');
    const backdrop = root.querySelector('.floating-contact__backdrop');
    const launcher = root.querySelector('[data-contact-open]');
    const closeButtons = root.querySelectorAll('[data-contact-close]');
    const channelButtons = root.querySelectorAll('[data-channel-toggle]');
    const copyButtons = root.querySelectorAll('[data-copy-value]');
    const liveRegion = root.querySelector('[data-contact-live]');

    if (!panel || !backdrop || !launcher) return;

    const controller = new AbortController();
    const { signal } = controller;
    let lastFocusedElement = null;
    let previousBodyOverflow = '';
    let copyTimer = null;
    let closeTimer = null;
    let openFrame = null;
    let focusFrame = null;
    const animationEnabled = root.dataset.animationEnabled === 'true' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    root.dataset.contactInitialized = 'true';

    const isOpen = () => panel.getAttribute('aria-hidden') === 'false';
    const getFocusableElements = () => Array.from(
      panel.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
    ).filter((element) => !element.hidden && element.offsetParent !== null);

    const selectChannel = (channel, focus = true) => {
      channelButtons.forEach((button) => {
        const selected = button.dataset.channelToggle === channel;
        button.setAttribute('aria-expanded', String(selected));
        const channelPanel = root.querySelector(`[data-channel-panel="${button.dataset.channelToggle}"]`);
        if (channelPanel) channelPanel.hidden = !selected;
      });

      if (focus) {
        const selectedButton = root.querySelector(`[data-channel-toggle="${channel}"]`);
        if (selectedButton) selectedButton.focus();
      }
    };

    const preferredChannel = root.querySelector(`[data-channel-toggle="${root.dataset.defaultChannel}"]`) || channelButtons[0];
    if (preferredChannel) selectChannel(preferredChannel.dataset.channelToggle, false);

    const showPanel = () => {
      if (isOpen()) return;
      window.clearTimeout(closeTimer);
      window.cancelAnimationFrame(openFrame);
      window.cancelAnimationFrame(focusFrame);
      lastFocusedElement = document.activeElement;
      previousBodyOverflow = document.body.style.overflow;
      panel.hidden = false;
      backdrop.hidden = false;
      panel.setAttribute('aria-hidden', 'false');
      launcher.setAttribute('aria-expanded', 'true');
      document.body.classList.add('floating-contact-open');
      openFrame = window.requestAnimationFrame(() => {
        if (!isOpen()) return;
        root.classList.add('is-open');
        focusFrame = window.requestAnimationFrame(() => {
          if (!isOpen()) return;
          panel.focus();
        });
      });
    };

    const hidePanel = (restoreFocus = true) => {
      if (!isOpen()) return;
      window.cancelAnimationFrame(openFrame);
      window.cancelAnimationFrame(focusFrame);
      panel.setAttribute('aria-hidden', 'true');
      root.classList.remove('is-open');
      launcher.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('floating-contact-open');
      document.body.style.overflow = previousBodyOverflow;
      const finishClose = () => {
        panel.hidden = true;
        backdrop.hidden = true;
      };
      if (animationEnabled) closeTimer = window.setTimeout(finishClose, 240);
      else finishClose();
      if (restoreFocus) {
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') lastFocusedElement.focus();
        else launcher.focus();
      }
    };

    const copyValue = async (button) => {
      const value = button.dataset.copyValue;
      const originalLabel = button.dataset.copyLabel || 'Copy';
      const copiedLabel = button.dataset.copiedLabel || 'Copied';
      const failedLabel = button.dataset.copyFailedLabel || 'Copy failed';
      const label = button.querySelector('[data-copy-text]');

      try {
        await navigator.clipboard.writeText(value);
        if (label) label.textContent = copiedLabel;
        if (liveRegion) liveRegion.textContent = copiedLabel;
        window.clearTimeout(copyTimer);
        copyTimer = window.setTimeout(() => {
          if (label) label.textContent = originalLabel;
          if (liveRegion) liveRegion.textContent = '';
        }, 2400);
      } catch (error) {
        if (liveRegion) liveRegion.textContent = failedLabel;
        window.prompt(originalLabel, value);
      }
    };

    launcher.addEventListener('click', showPanel, { signal });
    closeButtons.forEach((button) => button.addEventListener('click', () => hidePanel(), { signal }));
    channelButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        const keyboardActivated = event.detail === 0;
        selectChannel(button.dataset.channelToggle, keyboardActivated);
        if (!keyboardActivated) panel.focus({ preventScroll: true });
      }, { signal });
    });
    copyButtons.forEach((button) => button.addEventListener('click', () => copyValue(button), { signal }));

    document.addEventListener('keydown', (event) => {
      if (!isOpen()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        hidePanel();
        return;
      }
      if (event.key !== 'Tab') return;

      const focusable = getFocusableElements();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (document.activeElement === panel || !panel.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }, { signal });

    cleanups.set(root, () => {
      hidePanel(false);
      window.clearTimeout(copyTimer);
      window.clearTimeout(closeTimer);
      window.cancelAnimationFrame(openFrame);
      window.cancelAnimationFrame(focusFrame);
      controller.abort();
      delete root.dataset.contactInitialized;
      cleanups.delete(root);
    });
  };

  const initializeAll = (scope = document) => getWidgetRoots(scope).forEach(initializeWidget);
  const destroyAll = (scope) => getWidgetRoots(scope).forEach((root) => cleanups.get(root)?.());

  window[namespace] = { initializeAll, destroyAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyAll(event.target));
})();
